import React, { useState, useEffect, useContext } from "react";
import openSocket from "../../services/socket-io";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import { Tabs, Tab, Box } from "@material-ui/core";
import { toast } from "react-toastify";
import SaveIcon from "@material-ui/icons/Save";
import DeleteIcon from "@material-ui/icons/Delete";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import SyncIcon from "@material-ui/icons/Sync";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import ScheduleEditor from "../../components/ScheduleEditor";

import api from "../../services/api";
import { i18n } from "../../translate/i18n.js";
import toastError from "../../errors/toastError";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext.js";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		alignItems: "center",
		padding: theme.spacing(8, 8, 3),
	},

	paper: {
		padding: theme.spacing(2),
		display: "flex",
		alignItems: "center",
		marginBottom: 12,
	},

	settingOption: {
		marginLeft: "auto",
	},
	margin: {
		margin: theme.spacing(1),
	},
	
	sectionTitle: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(1),
	},
    
	paperStoreName: {
		padding: theme.spacing(2),
		marginBottom: 12,
	},

	storeNameContainer: {
		display: "flex",
		flexDirection: "column",
		width: "100%",
	},
    
	updateButtonContainer: {
		display: "flex",
		justifyContent: "flex-end",
		marginTop: theme.spacing(1),
	},
    
	storeNameField: {
		marginBottom: theme.spacing(1),
	},
	tabs: {
		marginBottom: theme.spacing(2),
	},
	tabPanel: {
		paddingTop: theme.spacing(2),
	},
}));

function TabPanel(props) {
	const { children, value, index, ...other } = props;
	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`settings-tabpanel-${index}`}
			aria-labelledby={`settings-tab-${index}`}
			{...other}
		>
			{value === index && <Box>{children}</Box>}
		</div>
	);
}

const Settings = () => {
	const classes = useStyles();
	const { whatsApps, loading } = useContext(WhatsAppsContext);

	const [settings, setSettings] = useState([]);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [selectedValue, setSelectedValue] = useState("");
	const [selectedKey, setSelectedKey] = useState("");
	const [storeName, setStoreName] = useState("");
	const [storeNameChanged, setStoreNameChanged] = useState(false);
	const [tabValue, setTabValue] = useState(0);
	const [chatbotSchedule, setChatbotSchedule] = useState({});
	const [dialogflowConfig, setDialogflowConfig] = useState({
		credentialsExists: false,
		projectId: "",
		location: "",
		agentId: "",
		languageCode: "es"
	});
	const [credentialsFile, setCredentialsFile] = useState(null);
	const [uploadingCredentials, setUploadingCredentials] = useState(false);
	const [syncConnectionName, setSyncConnectionName] = useState("");
	const [syncingContacts, setSyncingContacts] = useState(false);

	// Auto-select single connection for contact sync
	useEffect(() => {
		if (whatsApps && whatsApps.length === 1 && !syncConnectionName) {
			setSyncConnectionName(whatsApps[0].name);
		}
	}, [whatsApps, syncConnectionName]);

	useEffect(() => {
		const fetchSession = async () => {
			try {
				const { data } = await api.get("/settings");
				setSettings(data);
				
				// Initialize the store name from settings
				const storeNameSetting = data.find(s => s.key === "storeName");
				if (storeNameSetting) {
					setStoreName(storeNameSetting.value);
				}

				// Initialize chatbot schedule from settings
				const scheduleSetting = data.find(s => s.key === "chatbotScheduleJson");
				if (scheduleSetting && scheduleSetting.value) {
					try {
						const parsed = JSON.parse(scheduleSetting.value);
						setChatbotSchedule(parsed);
					} catch (e) {
						// If parsing fails, use default empty schedule
						setChatbotSchedule({});
					}
				}

			} catch (err) {
				toastError(err);
			}
		};
		fetchSession();
	}, []);

	const loadDialogflowConfig = async () => {
		try {
			const { data } = await api.get("/chatbot-config/dialogflow");
			if (data) {
				setDialogflowConfig({
					credentialsExists: data.credentialsExists || false,
					projectId: data.dialogflowProjectId || "",
					location: data.dialogflowLocation || "",
					agentId: data.dialogflowAgentId || "",
					languageCode: data.dialogflowLanguageCode || "es"
				});
			}
		} catch (err) {
			// Don't show error if it's just a 404 or auth issue - config might not exist yet
			if (err.response?.status !== 404 && err.response?.status !== 401 && err.response?.status !== 403) {
				toastError(err);
			}
			// Set defaults on error
			setDialogflowConfig({
				credentialsExists: false,
				projectId: "",
				location: "",
				agentId: "",
				languageCode: "es"
			});
		}
	};

	// Load Dialogflow config after settings are loaded
	useEffect(() => {
		if (settings.length > 0) {
			loadDialogflowConfig();
		}
	}, [settings.length]);

	useEffect(() => {
		const socket = openSocket();

		socket.on("settings", data => {
			if (data && data.action === "update" && data.setting) {
				setSettings(prevState => {
					const aux = [...prevState];
					const settingIndex = aux.findIndex(s => s.key === data.setting.key);
					if (settingIndex !== -1) {
						aux[settingIndex].value = data.setting.value;
					} else {
						aux.push(data.setting);
					}
					
					// Update local store name state if that's what changed
					if (data.setting.key === "storeName") {
						setStoreName(data.setting.value);
						setStoreNameChanged(false);
					}
					
					// Reload Dialogflow config if related settings changed
					if (data.setting.key && data.setting.key.startsWith("dialogflow")) {
						loadDialogflowConfig();
					}
					
					return aux;
				});
			}
		});

		return () => {
			socket.disconnect();
		};
	}, []);

	const handleChangeSetting = async (e) => {
		const value = e.target.value;
		const key = e.target.name;

		// Check if this is the store toggle setting
		if (key === "storeEnabled") {
			setSelectedValue(value);
			setSelectedKey(key);
			setConfirmOpen(true);
			return;
		}
		
		// Don't send API requests for store name changes, just update local state
		if (key === "storeName") {
			setStoreName(value);
			setStoreNameChanged(true);
			return;
		}

		try {
			await api.put(`/settings/${key}`, {
				value: value,
			});
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	const handleUpdateStoreName = async () => {
		try {
			await api.put(`/settings/storeName`, {
				value: storeName,
			});
			toast.success(i18n.t("settings.success"));
			setStoreNameChanged(false);
		} catch (err) {
			toastError(err);
		}
	};

	const handleConfirmSetting = async () => {
		try {
			await api.put(`/settings/${selectedKey}`, {
				value: selectedValue,
			});
			toast.success(i18n.t("settings.success"));
			setConfirmOpen(false);
		} catch (err) {
			toastError(err);
		}
	};

	const handleCancelSetting = () => {
		setConfirmOpen(false);
	};

	const getSettingValue = key => {
		const setting = settings.find(s => s.key === key);
		return setting ? setting.value : "";
	};

	const handleTabChange = (event, newValue) => {
		setTabValue(newValue);
	};

	const handleChatbotConfigChange = async (key, value) => {
		try {
			await api.put(`/settings/${key}`, { value });
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	const handleScheduleChange = (newSchedule) => {
		setChatbotSchedule(newSchedule);
	};

	const saveChatbotSchedule = async (scheduleToSave) => {
		try {
			const schedule = scheduleToSave || chatbotSchedule;
			const scheduleJson = JSON.stringify(schedule);
			await api.put(`/settings/chatbotScheduleJson`, { value: scheduleJson });
			toast.success("Horario guardado exitosamente");
		} catch (err) {
			toastError(err);
		}
	};

	const handleCredentialsUpload = async () => {
		if (!credentialsFile) {
			toast.error("Por favor seleccione un archivo");
			return;
		}

		setUploadingCredentials(true);
		try {
			const formData = new FormData();
			formData.append("credentials", credentialsFile);

			await api.post("/chatbot-config/credentials", formData, {
				headers: {
					"Content-Type": "multipart/form-data"
				}
			});

			toast.success("Credenciales subidas exitosamente");
			setCredentialsFile(null);
			await loadDialogflowConfig();
		} catch (err) {
			toastError(err);
		} finally {
			setUploadingCredentials(false);
		}
	};

	const handleDeleteCredentials = async () => {
		if (!window.confirm("¿Está seguro que desea eliminar las credenciales?")) {
			return;
		}

		try {
			await api.delete("/chatbot-config/credentials");
			toast.success("Credenciales eliminadas");
			await loadDialogflowConfig();
		} catch (err) {
			toastError(err);
		}
	};

	const handleDialogflowConfigSave = async () => {
		try {
			await api.put("/chatbot-config/dialogflow", {
				projectId: dialogflowConfig.projectId,
				location: dialogflowConfig.location,
				agentId: dialogflowConfig.agentId,
				languageCode: dialogflowConfig.languageCode
			});
			toast.success("Configuración de Dialogflow guardada");
			await loadDialogflowConfig();
		} catch (err) {
			toastError(err);
		}
	};

	const handleSyncContacts = async () => {
		const session = whatsApps && whatsApps.length === 1 ? whatsApps[0].name : syncConnectionName;
		if (!session) {
			toast.error("Seleccione una conexión");
			return;
		}
		setSyncingContacts(true);
		try {
			const { data } = await api.post("/contacts/sync", { session });
			toast.success(
				`Sincronización completada: ${data.created || 0} creados, ${data.updated || 0} actualizados.`
			);
		} catch (err) {
			toastError(err);
		} finally {
			setSyncingContacts(false);
		}
	};

	return (
		<div className={classes.root}>
			<Container className={classes.container} maxWidth={tabValue === 2 ? "md" : "sm"}>
				<Typography variant="body2" gutterBottom>
					{i18n.t("settings.title")}
				</Typography>

				<Paper>
					<Tabs value={tabValue} onChange={handleTabChange} indicatorColor="primary" textColor="primary">
						<Tab label="General" />
						<Tab label="Tienda" />
						<Tab label="Chatbot" />
					</Tabs>
				</Paper>

				{/* General Tab */}
				<TabPanel value={tabValue} index={0} className={classes.tabPanel}>
					<Paper className={classes.paper}>
					<Typography variant="body1">
						{i18n.t("settings.settings.userCreation.name")}
					</Typography>
					<Select
						margin="dense"
						variant="outlined"
						native
						id="userCreation-setting"
						name="userCreation"
						value={
							settings && settings.length > 0 && getSettingValue("userCreation")
						}
						className={classes.settingOption}
						onChange={handleChangeSetting}
					>
						<option value="enabled">
							{i18n.t("settings.settings.userCreation.options.enabled")}
						</option>
						<option value="disabled">
							{i18n.t("settings.settings.userCreation.options.disabled")}
						</option>
					</Select>
				</Paper>

				<Paper className={classes.paper}>
					<TextField
						id="api-token-setting"
						readOnly
						label="Token Api"
						margin="dense"
						variant="outlined"
						fullWidth
						value={settings && settings.length > 0 && getSettingValue("userApiToken")}
					/>
				</Paper>

				<Paper className={classes.paper}>
					<Typography variant="body1">
						Sincronizar Contactos
					</Typography>
					{whatsApps && whatsApps.length > 1 && (
						<Select
							margin="dense"
							variant="outlined"
							native
							id="sync-contacts-connection"
							value={syncConnectionName}
							onChange={(e) => setSyncConnectionName(e.target.value)}
							className={classes.settingOption}
							style={{ minWidth: 200 }}
						>
							<option value="">Seleccionar conexión</option>
							{whatsApps.map((wa) => (
								<option key={wa.id} value={wa.name}>
									{wa.name}
								</option>
							))}
						</Select>
					)}
					<Button
						variant="contained"
						color="primary"
						size="small"
						className={classes.margin}
						startIcon={<SyncIcon />}
						onClick={handleSyncContacts}
						disabled={
							syncingContacts ||
							!whatsApps ||
							whatsApps.length === 0 ||
							(whatsApps.length > 1 && !syncConnectionName)
						}
					>
						{syncingContacts ? "Sincronizando…" : "Sincronizar Contactos"}
					</Button>
				</Paper>

				</TabPanel>

				{/* Store Tab */}
				<TabPanel value={tabValue} index={1} className={classes.tabPanel}>
					<Typography variant="subtitle1" className={classes.sectionTitle}>
						Configuración de Tienda
					</Typography>

					<Paper className={classes.paper}>
						<Typography variant="body1">
							Estado de Tienda
						</Typography>
						<Select
							margin="dense"
							variant="outlined"
							native
							id="storeEnabled-setting"
							name="storeEnabled"
							value={
								settings && settings.length > 0 && getSettingValue("storeEnabled")
							}
							className={classes.settingOption}
							onChange={handleChangeSetting}
						>
							<option value="enabled">Activada</option>
							<option value="disabled">Desactivada</option>
						</Select>
					</Paper>

					{settings && 
					settings.length > 0 && 
					getSettingValue("storeEnabled") === "enabled" && (
						<>
							<Paper className={classes.paperStoreName}>
								<div className={classes.storeNameContainer}>
									<Typography variant="body1" gutterBottom>
										Nombre de Tienda
									</Typography>
									<TextField
										id="storeName-setting"
										margin="dense"
										variant="outlined"
										fullWidth
										name="storeName"
										value={storeName}
										onChange={handleChangeSetting}
										className={classes.storeNameField}
									/>
									<div className={classes.updateButtonContainer}>
										<Button
											variant="contained"
											color="primary"
											size="small"
											startIcon={<SaveIcon />}
											onClick={handleUpdateStoreName}
											disabled={!storeNameChanged}
										>
											Actualizar
										</Button>
									</div>
								</div>
							</Paper>

							<Paper className={classes.paper}>
								<Typography variant="body1">
									Conexión para notificaciones
								</Typography>
								<Select
									margin="dense"
									variant="outlined"
									native
									id="storeDefaultConnection-setting"
									name="storeDefaultConnection"
									value={
										settings && 
										settings.length > 0 && 
										getSettingValue("storeDefaultConnection")
									}
									className={classes.settingOption}
									onChange={handleChangeSetting}
								>
									<option value="Por defecto">Por Defecto</option>
									{whatsApps && 
										whatsApps.map((whatsapp) => (
											<option key={whatsapp.id} value={whatsapp.name}>
												{whatsapp.name}
											</option>
										))}
								</Select>
							</Paper>
						</>
					)}
				</TabPanel>

				{/* Chatbot Tab */}
				<TabPanel value={tabValue} index={2} className={classes.tabPanel}>
					<Typography variant="subtitle1" className={classes.sectionTitle}>
						Configuración de Chatbot
					</Typography>

					<Paper className={classes.paper}>
						<Typography variant="body1">
							Habilitar Chatbot (Global)
						</Typography>
						<Select
							margin="dense"
							variant="outlined"
							native
							id="chatbotEnabled-setting"
							name="chatbotEnabled"
							value={getSettingValue("chatbotEnabled") || "disabled"}
							className={classes.settingOption}
							onChange={(e) => handleChatbotConfigChange("chatbotEnabled", e.target.value)}
						>
							<option value="enabled">Activado</option>
							<option value="disabled">Desactivado</option>
						</Select>
					</Paper>

					<Paper className={classes.paper}>
						<Typography variant="body1">
							Tipo de Activación
						</Typography>
						<Select
							margin="dense"
							variant="outlined"
							native
							id="chatbotActivationType-setting"
							name="chatbotActivationType"
							value={getSettingValue("chatbotActivationType") || "simple"}
							className={classes.settingOption}
							onChange={(e) => handleChatbotConfigChange("chatbotActivationType", e.target.value)}
						>
							<option value="simple">Siempre Activo</option>
							<option value="schedule">Por Horario</option>
						</Select>
					</Paper>

					{getSettingValue("chatbotActivationType") === "schedule" && (
						<>
							<Paper className={classes.paperStoreName}>
								<Typography variant="h6" gutterBottom style={{ marginBottom: 24 }}>
									Horario del Chatbot
								</Typography>
								<ScheduleEditor
									schedule={chatbotSchedule}
									onChange={handleScheduleChange}
									onSave={saveChatbotSchedule}
								/>
							</Paper>

							<Paper className={classes.paper}>
								<Typography variant="body1">
									Zona Horaria
								</Typography>
								<Select
									margin="dense"
									variant="outlined"
									native
									id="chatbotTimezone-setting"
									name="chatbotTimezone"
									value={getSettingValue("chatbotTimezone") || "America/Managua"}
									className={classes.settingOption}
									onChange={(e) => handleChatbotConfigChange("chatbotTimezone", e.target.value)}
								>
									<option value="America/Managua">America/Managua</option>
									<option value="America/Mexico_City">America/Mexico_City</option>
									<option value="America/Bogota">America/Bogota</option>
									<option value="America/Lima">America/Lima</option>
									<option value="America/Santiago">America/Santiago</option>
									<option value="America/Buenos_Aires">America/Buenos_Aires</option>
									<option value="America/Sao_Paulo">America/Sao_Paulo</option>
									<option value="UTC">UTC</option>
								</Select>
							</Paper>
						</>
					)}

					{/* Dialogflow Configuration */}
					<Typography variant="h6" className={classes.sectionTitle} style={{ marginTop: 32 }}>
						Configuración de Dialogflow
					</Typography>

					<Paper className={classes.paperStoreName}>
						<Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
							<Typography variant="body1" style={{ fontWeight: 500 }}>
								Credenciales de Google Cloud
							</Typography>
							{dialogflowConfig.credentialsExists ? (
								<Box display="flex" alignItems="center" color="success.main">
									<CheckCircleIcon style={{ marginRight: 4 }} />
									<Typography variant="body2" color="inherit">
										Configurado
									</Typography>
								</Box>
							) : (
								<Box display="flex" alignItems="center" color="error.main">
									<ErrorIcon style={{ marginRight: 4 }} />
									<Typography variant="body2" color="inherit">
										No configurado
									</Typography>
								</Box>
							)}
						</Box>

						<input
							accept=".json,application/json"
							style={{ display: "none" }}
							id="credentials-file-upload"
							type="file"
							onChange={(e) => {
								if (e.target.files && e.target.files[0]) {
									setCredentialsFile(e.target.files[0]);
								}
							}}
						/>
						<label htmlFor="credentials-file-upload">
							<Button
								variant="outlined"
								component="span"
								startIcon={<CloudUploadIcon />}
								style={{ marginRight: 8, marginBottom: 8 }}
							>
								{credentialsFile ? credentialsFile.name : "Seleccionar Archivo JSON"}
							</Button>
						</label>

						{dialogflowConfig.credentialsExists && (
							<Button
								variant="outlined"
								color="secondary"
								startIcon={<DeleteIcon />}
								onClick={handleDeleteCredentials}
								style={{ marginLeft: 8, marginBottom: 8 }}
							>
								Eliminar
							</Button>
						)}

						{credentialsFile && (
							<Box mt={1}>
								<Button
									variant="contained"
									color="primary"
									onClick={handleCredentialsUpload}
									disabled={uploadingCredentials}
									startIcon={<SaveIcon />}
								>
									{uploadingCredentials ? "Subiendo..." : "Subir Credenciales"}
								</Button>
							</Box>
						)}

						<Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 8 }}>
							Suba el archivo JSON de credenciales de servicio de Google Cloud Console
						</Typography>
					</Paper>

					<Paper className={classes.paperStoreName}>
						<Typography variant="body1" gutterBottom style={{ fontWeight: 500, marginBottom: 16 }}>
							Configuración de Dialogflow CX
						</Typography>

						<TextField
							fullWidth
							label="Project ID"
							value={dialogflowConfig.projectId || ""}
							onChange={(e) => setDialogflowConfig({ ...dialogflowConfig, projectId: e.target.value })}
							margin="dense"
							variant="outlined"
							placeholder="your-project-id"
							style={{ marginBottom: 12 }}
						/>

						<TextField
							fullWidth
							label="Location"
							value={dialogflowConfig.location || ""}
							onChange={(e) => setDialogflowConfig({ ...dialogflowConfig, location: e.target.value })}
							margin="dense"
							variant="outlined"
							placeholder="us-central1"
							style={{ marginBottom: 12 }}
						/>

						<TextField
							fullWidth
							label="Agent ID"
							value={dialogflowConfig.agentId || ""}
							onChange={(e) => setDialogflowConfig({ ...dialogflowConfig, agentId: e.target.value })}
							margin="dense"
							variant="outlined"
							placeholder="your-agent-id"
							style={{ marginBottom: 12 }}
						/>

						<Box style={{ marginBottom: 16 }}>
							<Typography variant="body2" style={{ marginBottom: 8 }}>
								Language Code
							</Typography>
							<Select
								fullWidth
								margin="dense"
								variant="outlined"
								native
								value={dialogflowConfig.languageCode || "es"}
								onChange={(e) => setDialogflowConfig({ ...dialogflowConfig, languageCode: e.target.value })}
							>
								<option value="es">Español (es)</option>
								<option value="en">English (en)</option>
								<option value="pt">Português (pt)</option>
								<option value="fr">Français (fr)</option>
							</Select>
						</Box>

						<Button
							variant="contained"
							color="primary"
							onClick={handleDialogflowConfigSave}
							startIcon={<SaveIcon />}
							style={{ marginTop: 8 }}
						>
							Guardar Configuración
						</Button>
					</Paper>

					<Paper className={classes.paper}>
						<Typography variant="body1">
							Conexión por Defecto para Mensajes API
						</Typography>
						<Select
							margin="dense"
							variant="outlined"
							native
							id="apiMessagesDefaultConnection-setting"
							name="apiMessagesDefaultConnection"
							value={getSettingValue("apiMessagesDefaultConnection") || "Por defecto"}
							className={classes.settingOption}
							onChange={(e) => handleChatbotConfigChange("apiMessagesDefaultConnection", e.target.value)}
						>
							<option value="Por defecto">Por Defecto</option>
							{whatsApps && 
								whatsApps.map((whatsapp) => (
									<option key={whatsapp.id} value={whatsapp.name}>
										{whatsapp.name}
									</option>
								))}
						</Select>
					</Paper>

					<Paper className={classes.paper}>
						<Typography variant="body1">
							Conexión para Alertas
						</Typography>
						<Select
							margin="dense"
							variant="outlined"
							native
							id="alertasConnectionName-setting"
							name="alertasConnectionName"
							value={getSettingValue("alertasConnectionName") || "Por defecto"}
							className={classes.settingOption}
							onChange={(e) => handleChatbotConfigChange("alertasConnectionName", e.target.value)}
						>
							<option value="Por defecto">Por Defecto</option>
							{whatsApps && 
								whatsApps.map((whatsapp) => (
									<option key={whatsapp.id} value={whatsapp.name}>
										{whatsapp.name}
									</option>
								))}
						</Select>
					</Paper>
				</TabPanel>

				{/* Confirmation Dialog */}
				<Dialog
					open={confirmOpen}
					onClose={handleCancelSetting}
					aria-labelledby="alert-dialog-title"
					aria-describedby="alert-dialog-description"
				>
					<DialogTitle id="alert-dialog-title">
						{"Confirmar Cambio de Configuración"}
					</DialogTitle>
					<DialogContent>
						<DialogContentText id="alert-dialog-description">
							¿Está seguro que desea {selectedValue === "enabled" ? "activar" : "desactivar"} la tienda?
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleCancelSetting} color="primary">
							Cancelar
						</Button>
						<Button onClick={handleConfirmSetting} color="primary" autoFocus>
							Confirmar
						</Button>
					</DialogActions>
				</Dialog>
			</Container>
		</div>
	);
};

export default Settings;